import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    FiUser, FiKey, FiLogOut, FiEdit3,
    FiMapPin, FiPhone, FiMail, FiHome,
    FiSave, FiX, FiUsers, FiPlus, FiTrash2
} from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import { useTranslation } from 'react-i18next';

const Account = () => {
    const { t } = useTranslation();
    const [user, setUser] = useState(null);
    const [accountInfo, setAccountInfo] = useState(null);
    const [editingUser, setEditingUser] = useState(false);
    const [editingInfo, setEditingInfo] = useState(false);
    const [username, setUsername] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [nom, setNom] = useState('');
    const [location, setLocation] = useState('');
    const [phone1, setPhone1] = useState('');
    const [phone2, setPhone2] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoUrl, setLogoUrl] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('success');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [conditions, setConditions] = useState([]);
    const [newConditionFr, setNewConditionFr] = useState('');
    const [newConditionAr, setNewConditionAr] = useState('');

    // Employee management states
    const [employees, setEmployees] = useState([]);
    const [showEmployeeModal, setShowEmployeeModal] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [employeeForm, setEmployeeForm] = useState({
        username: '',
        password: '',
        role: 'employe'
    });
    const [employeeLoading, setEmployeeLoading] = useState(false);

    const token = localStorage.getItem('token');

    useEffect(() => {
        if (!token) {
            window.location.href = '/';
            return;
        }

        const fetchData = async () => {
            try {
                const [userRes, infoRes] = await Promise.all([
                    axios.get('https://krili-loto.com/api/me', {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get('https://krili-loto.com/api/account-info')
                ]);

                setUser(userRes.data);
                setUsername(userRes.data.username);
                setAccountInfo(infoRes.data);
                setNom(infoRes.data.nom || '');
                setLocation(infoRes.data.location || '');
                setPhone1(infoRes.data.phone1 || '');
                setPhone2(infoRes.data.phone2 || '');
                setEmail(infoRes.data.email || '');
                setAddress(infoRes.data.address || '');
                
                // Parse and set conditions
                try {
                    const parsedConditions = infoRes.data.conditions ? 
                        (typeof infoRes.data.conditions === 'string' ? JSON.parse(infoRes.data.conditions) : infoRes.data.conditions) 
                        : [];
                    setConditions(Array.isArray(parsedConditions) ? parsedConditions : []);
                } catch (e) {
                    console.error('Error parsing conditions:', e);
                    setConditions([]);
                }
                if (infoRes.data.logo) {
                    setLogoUrl(`https://krili-loto.com/api/uploads/${infoRes.data.logo}`);
                }

                // Fetch employees if user is admin
                if (userRes.data.role === 'admin') {
                    await fetchEmployees();
                }
            } catch (err) {
                if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                    localStorage.removeItem('token');
                    window.location.href = '/';
                } else {
                    showMessage(t('error_loading_data'), 'error');
                }
            }
        };

        fetchData();
    }, [token]);

    const fetchEmployees = async () => {
        try {
            const response = await axios.get('https://krili-loto.com/api/all', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEmployees(response.data);
        } catch (err) {
            showMessage(t('error_loading_employees'), 'error');
        }
    };

    const showMessage = (msg, type = 'success') => {
        setMessage(msg);
        setMessageType(type);
        setTimeout(() => setMessage(''), 5000);
    };

    const updateUser = async () => {
        try {
            await axios.put('https://krili-loto.com/api/me', {
                username,
                role: user.role
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(prev => ({ ...prev, username }));
            showMessage(t('username_updated'));
            setEditingUser(false);
        } catch (err) {
            showMessage(t('update_error'), 'error');
        }
    };

    const changePassword = async () => {
        if (!currentPassword || !password) {
            showMessage(t('password_fields_required'), 'error');
            return;
        }

        setPasswordLoading(true);
        try {
            await axios.put('https://krili-loto.com/api/me/password', {
                currentPassword,
                newPassword: password
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            showMessage(t('password_updated'));
            setCurrentPassword('');
            setPassword('');
        } catch (err) {
            showMessage(err.response?.data?.message || t('update_error'), 'error');
        } finally {
            setPasswordLoading(false);
        }
    };

    const updateAccountInfo = async () => {
        try {
            const formData = new FormData();
            formData.append('nom', nom);
            formData.append('location', location);
            formData.append('phone1', phone1);
            formData.append('phone2', phone2);
            formData.append('email', email);
            formData.append('address', address);
            formData.append('conditions', JSON.stringify(conditions));
            if (logoFile) {
                formData.append('logo', logoFile);
            }

            const response = await axios.put('https://krili-loto.com/api/account-info', formData);
            showMessage(t('info_updated'));
            if (response.data.logo) {
                setLogoUrl(`https://krili-loto.com/api/uploads/${response.data.logo}`);
                setLogoFile(null);
            }
            setEditingInfo(false);
        } catch (err) {
            showMessage(t('update_error'), 'error');
        }
    };

    // Employee management functions
    const handleEmployeeSubmit = async (e) => {
        e.preventDefault();
        setEmployeeLoading(true);

        try {
            if (editingEmployee) {
                // Update existing employee
                await axios.put(`https://krili-loto.com/api/${editingEmployee.id}`, {
                    username: employeeForm.username,
                    role: employeeForm.role
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showMessage(t('employee_updated'));
            } else {
                // Create new employee
                await axios.post('https://krili-loto.com/api/create', employeeForm, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showMessage(t('employee_created'));
            }

            await fetchEmployees();
            setShowEmployeeModal(false);
            setEditingEmployee(null);
            setEmployeeForm({ username: '', password: '', role: 'employe' });
        } catch (err) {
            showMessage(err.response?.data?.message || t('operation_error'), 'error');
        } finally {
            setEmployeeLoading(false);
        }
    };

    const handleEditEmployee = (employee) => {
        setEditingEmployee(employee);
        setEmployeeForm({
            username: employee.username,
            password: '', // Don't pre-fill password for security
            role: employee.role
        });
        setShowEmployeeModal(true);
    };

    const handleDeleteEmployee = async (employeeId) => {
        if (!window.confirm(t('confirm_delete_employee'))) {
            return;
        }

        try {
            await axios.delete(`https://krili-loto.com/api/${employeeId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showMessage(t('employee_deleted'));
            await fetchEmployees();
        } catch (err) {
            showMessage(err.response?.data?.message || t('update_error'), 'error');
        }
    };

    const resetEmployeeForm = () => {
        setEmployeeForm({ username: '', password: '', role: 'employe' });
        setEditingEmployee(null);
        setShowEmployeeModal(false);
    };

    const addCondition = () => {
        if (!newConditionFr.trim() && !newConditionAr.trim()) return;
        setConditions([...conditions, { fr: newConditionFr, ar: newConditionAr }]);
        setNewConditionFr('');
        setNewConditionAr('');
    };

    const removeCondition = (index) => {
        setConditions(conditions.filter((_, i) => i !== index));
    };

    const updateCondition = (index, field, value) => {
        const newConditions = [...conditions];
        newConditions[index][field] = value;
        setConditions(newConditions);
    };

    if (!user || !accountInfo) {
        return (
            <Sidebar>
                <div className="flex justify-center items-center h-screen">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            </Sidebar>
        );
    }

    return (
        <Sidebar>
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-8">
                        <h1 className="text-2xl font-bold text-gray-800">{t('my_account')}</h1>
                        <button
                            onClick={() => {
                                localStorage.removeItem('token');
                                window.location.href = '/';
                            }}
                            className="flex items-center gap-2 text-red-600 hover:text-red-800 transition-colors"
                        >
                            <FiLogOut /> {t('logout')}
                        </button>
                    </div>

                    {/* Message Alert */}
                    {message && (
                        <div className={`mb-6 p-4 rounded-lg ${messageType === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {message}
                        </div>
                    )}

                    {/* User Profile Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-6">
                                <div className="flex items-center gap-4">
                                    <div className="rounded-full bg-blue-600 text-white w-14 h-14 flex items-center justify-center text-xl font-bold">
                                        {user.username.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">{user.username}</h2>
                                        <p className="text-sm text-gray-500 capitalize">{user.role}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => editingUser ? updateUser() : setEditingUser(true)}
                                    className={`flex items-center gap-2 px-3 py-1 rounded-md ${editingUser ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50'}`}
                                >
                                    {editingUser ? (
                                        <>
                                            <FiSave size={14} /> {t('save_btn')}
                                        </>
                                    ) : (
                                        <>
                                            <FiEdit3 size={14} /> {t('modify')}
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Username Field */}
                                <div className="flex items-center gap-3">
                                    <FiUser className="text-gray-500 flex-shrink-0" />
                                    {editingUser ? (
                                        <input
                                            value={username}
                                            onChange={e => setUsername(e.target.value)}
                                            className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    ) : (
                                        <p className="text-gray-700">{username}</p>
                                    )}
                                </div>

                                {/* Password Fields */}
                                <div className="space-y-3 pt-4 border-t border-gray-100">
                                    <h3 className="font-medium text-gray-700 flex items-center gap-2">
                                        <FiKey className="text-gray-500" /> {t('change_password')}
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <input
                                            type="password"
                                            placeholder={t('current_password')}
                                            value={currentPassword}
                                            onChange={e => setCurrentPassword(e.target.value)}
                                            className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <input
                                            type="password"
                                            placeholder={t('new_password')}
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <button
                                        onClick={changePassword}
                                        disabled={!currentPassword || !password || passwordLoading}
                                        className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm ${passwordLoading || !currentPassword || !password ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                                    >
                                        {passwordLoading ? t('updating_password') : t('update_password_btn')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Account Info Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-lg font-semibold text-gray-800">{t('agency_info')}</h2>
                                <div className="flex gap-2">
                                    {editingInfo && (
                                        <button
                                            onClick={() => setEditingInfo(false)}
                                            className="flex items-center gap-2 px-3 py-1 rounded-md text-gray-600 hover:bg-gray-100"
                                        >
                                            <FiX size={14} /> {t('cancel')}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => editingInfo ? updateAccountInfo() : setEditingInfo(true)}
                                        className={`flex items-center gap-2 px-3 py-1 rounded-md ${editingInfo ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50'}`}
                                    >
                                        {editingInfo ? (
                                            <>
                                                <FiSave size={14} /> {t('save_btn')}
                                            </>
                                        ) : (
                                            <>
                                                <FiEdit3 size={14} /> {t('modify')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {[
                                    { icon: <FiUser className="text-gray-500" />, label: t('agency_name'), value: nom, setter: setNom },
                                    { icon: <FiMapPin className="text-gray-500" />, label: t('location_label'), value: location, setter: setLocation },
                                    { icon: <FiPhone className="text-gray-500" />, label: t('phone1'), value: phone1, setter: setPhone1 },
                                    { icon: <FiPhone className="text-gray-500" />, label: t('phone2'), value: phone2, setter: setPhone2 },
                                    { icon: <FiMail className="text-gray-500" />, label: t('email'), value: email, setter: setEmail },
                                    { icon: <FiHome className="text-gray-500" />, label: t('address'), value: address, setter: setAddress }
                                ].map(({icon, label, value, setter}) => (
                                    <div key={label} className="space-y-1">
                                        <label className="text-sm font-medium text-gray-600">{label}</label>
                                        <div className="flex items-center gap-2">
                                            {icon}
                                            {editingInfo ? (
                                                <input
                                                    type="text"
                                                    value={value}
                                                    onChange={e => setter(e.target.value)}
                                                    className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder={label}
                                                />
                                            ) : (
                                                <p className="text-gray-700">{value || '-'}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-600">Logo</label>
                                    <div className="flex flex-col gap-2">
                                        {(logoUrl || logoFile) && (
                                            <img
                                                src={logoFile ? URL.createObjectURL(logoFile) : logoUrl}
                                                alt="Logo Preview"
                                                style={{ width: '100%', maxWidth: '300px', height: 'auto', objectFit: 'contain' }}
                                                className="border rounded p-1"
                                            />
                                        )}
                                        {editingInfo && (
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={e => setLogoFile(e.target.files[0])}
                                                className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Contract Conditions Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                    <FiEdit3 className="text-gray-500" /> {t('contract_conditions')} / شروط العقد
                                </h2>
                                <button
                                    onClick={updateAccountInfo}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    <FiSave size={16} /> {t('save_btn')}
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Add New Condition Form */}
                                <div className="bg-gray-50 p-4 rounded-lg border border-dashed border-gray-300">
                                    <h3 className="text-sm font-medium text-gray-700 mb-3">{t('add_new_condition')}</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-xs text-gray-500">Français</label>
                                            <textarea
                                                value={newConditionFr}
                                                onChange={e => setNewConditionFr(e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                rows="2"
                                                placeholder="Texte en français..."
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs text-gray-500 text-right block">العربية</label>
                                            <textarea
                                                value={newConditionAr}
                                                onChange={e => setNewConditionAr(e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                                                dir="rtl"
                                                rows="2"
                                                placeholder="النص باللغة العربية..."
                                            />
                                        </div>
                                    </div>
                                    <button
                                        onClick={addCondition}
                                        disabled={!newConditionFr.trim() && !newConditionAr.trim()}
                                        className="mt-3 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
                                    >
                                        <FiPlus size={16} /> {t('add')}
                                    </button>
                                </div>

                                {/* List of Existing Conditions */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-medium text-gray-700">{t('existing_conditions')} ({conditions.length})</h3>
                                    {conditions.length === 0 ? (
                                        <p className="text-sm text-gray-500 italic">{t('no_conditions_added')}</p>
                                    ) : (
                                        <div className="space-y-3">
                                            {conditions.map((condition, index) => (
                                                <div key={index} className="flex gap-4 p-4 bg-white border border-gray-200 rounded-lg group hover:border-blue-300 transition-all">
                                                    <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-xs font-bold text-gray-600">
                                                        {index + 1}
                                                    </div>
                                                    <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div className="text-sm text-gray-700 border-r border-gray-100 pr-4">
                                                            <textarea
                                                                value={condition.fr}
                                                                onChange={e => updateCondition(index, 'fr', e.target.value)}
                                                                className="w-full border-none p-0 focus:ring-0 resize-none bg-transparent"
                                                                rows="2"
                                                            />
                                                        </div>
                                                        <div className="text-sm text-gray-700 text-right" dir="rtl">
                                                            <textarea
                                                                value={condition.ar}
                                                                onChange={e => updateCondition(index, 'ar', e.target.value)}
                                                                className="w-full border-none p-0 focus:ring-0 resize-none bg-transparent text-right"
                                                                rows="2"
                                                            />
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => removeCondition(index)}
                                                        className="flex-shrink-0 text-red-400 hover:text-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        title={t('delete')}
                                                    >
                                                        <FiTrash2 size={18} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                    </div>
                </div>


                    {/* Employee Management Section (Admin only) */}
                    {user.role === 'admin' && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                        <FiUsers className="text-gray-500" /> {t('employee_management')}
                                    </h2>
                                    <button
                                        onClick={() => setShowEmployeeModal(true)}
                                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                    >
                                        <FiPlus size={16} /> {t('new_employee')}
                                    </button>
                                </div>

                                {employees.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <FiUsers className="mx-auto text-4xl mb-2" />
                                        <p>{t('no_employees_found')}</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{t('username_col')}</th>
                                                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{t('role_col')}</th>
                                                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{t('created_at_col')}</th>
                                                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{t('actions_col')}</th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                            {employees.map((employee) => (
                                                <tr key={employee.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-sm text-gray-700">{employee.username}</td>
                                                    <td className="px-4 py-3">
                                                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${
                                                                employee.role === 'admin'
                                                                    ? 'bg-purple-100 text-purple-800'
                                                                    : 'bg-blue-100 text-blue-800'
                                                            }`}>
                                                                {employee.role}
                                                            </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">
                                                        {new Date(employee.created_at).toLocaleDateString('fr-FR')}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleEditEmployee(employee)}
                                                                className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                                                            >
                                                                <FiEdit3 size={14} /> {t('edit')}
                                                            </button>
                                                            {employee.id !== user.id && (
                                                                <button
                                                                    onClick={() => handleDeleteEmployee(employee.id)}
                                                                    className="flex items-center gap-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                                                                >
                                                                    <FiTrash2 size={14} /> {t('delete')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Employee Modal */}
                    {showEmployeeModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                            <div className="bg-white rounded-xl shadow-lg w-full max-w-md">
                                <div className="p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                        {editingEmployee ? t('edit_employee') : t('new_employee')}
                                    </h3>
                                    <form onSubmit={handleEmployeeSubmit} className="space-y-4">
                                            <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {t('username_col')}
                                            </label>
                                            <input
                                                type="text"
                                                value={employeeForm.username}
                                                onChange={e => setEmployeeForm({...employeeForm, username: e.target.value})}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                            />
                                        </div>
                                        {!editingEmployee && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {t('password_label')}
                                                </label>
                                                <input
                                                    type="password"
                                                    value={employeeForm.password}
                                                    onChange={e => setEmployeeForm({...employeeForm, password: e.target.value})}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    required
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {t('role_col')}
                                            </label>
                                            <select
                                                value={employeeForm.role}
                                                onChange={e => setEmployeeForm({...employeeForm, role: e.target.value})}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="employe">{t('role_employee')}</option>
                                                <option value="admin">{t('role_admin')}</option>
                                            </select>
                                        </div>
                                        <div className="flex gap-3 pt-4">
                                            <button
                                                type="button"
                                                onClick={resetEmployeeForm}
                                                className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                                            >
                                                {t('cancel')}
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={employeeLoading}
                                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                {employeeLoading ? t('creating_label') : (editingEmployee ? t('modify') : t('add'))}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Sidebar>
    );
};

export default Account;